import styled from 'styled-components';
import {CloseIcon, InfoIconComponent, MaximizeIcon, MinusIcon} from '@/components/Icons';
import {Typography, TypographyProps, typographyStyle} from '@/components/Typography';
import {BackgroundImage, Divider} from '@/styled-components';
import {interceptProps} from '@/styled-components/utils';
import {palette} from '@/styles/palette';
import {getHexWithOpacity} from '@/styles/utils';

export const Container = styled('div').withConfig({
  shouldForwardProp: interceptProps(['marginDirection']),
})<{
  marginDirection: 'left' | 'right';
}>`
  width: 280px;
  ${({marginDirection = 'left'}) => `margin-${marginDirection}: 16px;`}
  border-radius: 5px;
  border: solid 1px #bcbcbc;
  background-color: ${palette.white};
  color: ${palette.textPrimary};
  box-shadow: 0px 4px 10px 0px #00000026;
`;

export const WindowHeader = styled.div``;

export const WindowBody = styled.div`
  background-color: #f6f6f6;
  padding: 0 8px 8px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
`;

export const WindowFuncButton = styled(BackgroundImage)`
  cursor: pointer;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  & + & {
    margin-left: 4px;
  }
`;

export const SectionContainer = styled.section``;

export const SectionTitle = styled(Typography).attrs({
  as: 'div',
  variant: 'medium12',
})`
  color: ${palette.textPrimary};
  font-weight: 700;
  height: 36px;
  display: flex;
  align-items: center;
`;

export const Box = styled.div`
  border-radius: 5px;
  background-color: ${palette.white};
  ${Divider} {
    background-color: ${palette.gray100};
  }
`;

export const BoxGroup = styled.div`
  display: flex;
  gap: 4px;
  ${Box} {
    flex-grow: 1;
    flex-shrink: 0;
    flex-basis: 0;
  }
`;

export const BoxTitle = styled(Typography).attrs({
  as: 'div',
  variant: 'medium10',
})`
  color: ${palette.textSecondary};
  font-weight: 700;
  text-align: center;
`;

export const ValueText = styled(Typography).attrs({
  as: 'div',
  variant: 'medium16',
})`
  color: ${palette.textPrimary};
  font-weight: 700;
`;

export const ValueFormatText = styled(Typography).attrs({
  variant: 'medium10',
})`
  color: ${palette.textPrimary};
  font-weight: 700;
`;

export const SubText = styled(Typography).attrs(
  ({color, variant, fontWeight, lineHeight}: TypographyProps) => ({
    variant: variant ?? 'medium10',
    color: color ?? palette.textSecondary,
    fontWeight: fontWeight ?? 500,
    lineHeight: lineHeight ?? '14px',
  })
)``;

export const EmptyBox = styled(Box).withConfig({
  shouldForwardProp: interceptProps(['height']),
})<{
  height?: number;
}>`
  color: ${getHexWithOpacity(palette.textPrimary, 0.5)};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: ${({height = 60}) => `${height}px;`};
  font-weight: 500;
  ${{...typographyStyle.medium12}}
`;

export const ColorCircle = styled('div').withConfig({
  shouldForwardProp: interceptProps(['color']),
})<{
  color?: string;
}>`
  margin-right: 4px;
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 100%;
  ${({color}) => color && `background-color: ${color};`}
`;

export const WideButton = styled('button').withConfig({
  shouldForwardProp: interceptProps(['radius', 'teal', 'black']),
})<{
  radius?: boolean;
  teal?: boolean;
  black?: boolean;
}>`
  height: 24px;
  width: 100%;
  cursor: pointer;
  border: 0;
  ${{...typographyStyle.medium10}}
  font-weight: 700;
  ${({teal, black}) =>
    black
      ? `
  background-color: #3E4650;
  color: ${palette.white};
  `
      : teal
      ? `
  background-color: ${palette.teal200};
  color: ${palette.white};
`
      : `
  background-color: ${palette.gray150};
  color: ${palette.textPrimary};
`}
  ${({radius}) => radius && `border-radius: 5px;`}
  &:disabled {
    opacity: 0.3;
  }
`;

export const CloseButton = styled(WindowFuncButton)`
  background-image: url(${CloseIcon});
`;

export const MinimizeButton = styled(WindowFuncButton).withConfig({
  shouldForwardProp: interceptProps(['isMin']),
})<{
  isMin?: boolean;
}>`
  background-image: url(${({isMin}) => (isMin ? MaximizeIcon : MinusIcon)});
`;

export const SectionInfoIcon = styled(InfoIconComponent).attrs({
  color: palette.gray400,
})`
  margin-left: 2px;
  width: 16px;
  height: 16px;
  cursor: pointer;
`;
